import Parse from '../../parseConfig';
import { cleanImportWarnings, toMapCoordinates } from './builtProjectUtils';
import { getConsultantName } from './fetchSalesConsultants';

const getParseFileName = (file) => {
  if (!file) return '';

  const fileName = typeof file.name === 'function' ? file.name() : file._name || file.name || '';
  const separatorIndex = fileName.indexOf('_');
  return separatorIndex === -1 ? fileName : fileName.slice(separatorIndex + 1);
};

/**
 * Fetches built-home examples from Parse's BuiltHome class. BuiltHome uses one
 * GeoPoint column (`geocodedCoordinates`) for both geocoded and manually moved
 * locations because Parse Server's MongoDB adapter permits one GeoPoint per class.
 */
export async function fetchBuiltData() {
  const BuiltHome = Parse.Object.extend('BuiltHome');
  const pageSize = 500;
  const results = [];

  for (let offset = 0; ; offset += pageSize) {
    const query = new Parse.Query(BuiltHome);
    query.limit(pageSize);
    query.skip(offset);
    query.descending('createdAt');
    query.include('salesConsultant');
    query.include('basePlan');
    query.select([
      'address',
      'clientName',
      'buildStyle',
      'type',
      'completedYear',
      'consentedDate',
      'needsReview',
      'salesConsultant',
      'salesConsultantName',
      'basePlan',
      'basePlanIsCustom',
      'coordinates',
      'geocodedCoordinates',
      'photo',
      'plansPdf',
      'sourceImportKey',
      'sourceFolderName',
      'sourceJobNumber',
      'sourcePlanPath',
      'sourceDocumentHash',
      'addressKey',
      'importWarnings',
    ]);

    const page = await query.find();
    results.push(...page);
    if (page.length < pageSize) break;
  }

  return results.map((result) => {
    const storedCoordinates = result.get('geocodedCoordinates') || result.get('coordinates');
    const photo = result.get('photo');
    const plansPdf = result.get('plansPdf');
    const salesConsultant = result.get('salesConsultant');
    const basePlan = result.get('basePlan');
    const manualSalesConsultantName = result.get('salesConsultantName') || '';

    return {
      id: result.id,
      createdAt: result.createdAt ? result.createdAt.toISOString() : null,
      address: result.get('address') || '',
      clientName: result.get('clientName') || '',
      buildStyle: result.get('buildStyle') || '',
      type: result.get('type') || '',
      completedYear: result.get('completedYear') || null,
      consentedDate: result.get('consentedDate')?.toISOString?.() || null,
      needsReview: Number(result.get('needsReview') || 0),
      salesConsultantId: salesConsultant?.id || '',
      salesConsultantName: getConsultantName(salesConsultant) || manualSalesConsultantName,
      salesConsultantManualName: manualSalesConsultantName,
      basePlanId: basePlan?.id || '',
      basePlanName: basePlan?.get?.('name') || '',
      basePlanSize: basePlan?.get?.('size') ?? null,
      basePlanIsCustom: result.get('basePlanIsCustom') === true,
      coordinates: toMapCoordinates(storedCoordinates),
      photo: photo ? photo.url() : null,
      photoName: getParseFileName(photo),
      plansPdf: plansPdf ? plansPdf.url() : null,
      plansPdfName: getParseFileName(plansPdf),
      sourceImportKey: result.get('sourceImportKey') || '',
      sourceFolderName: result.get('sourceFolderName') || '',
      sourceJobNumber: result.get('sourceJobNumber') || '',
      sourcePlanPath: result.get('sourcePlanPath') || '',
      sourceDocumentHash: result.get('sourceDocumentHash') || '',
      addressKey: result.get('addressKey') || '',
      importWarnings: cleanImportWarnings(result.get('importWarnings')),
      // StockMapComponent uses this only as the marker's accessible title.
      displayTitle: result.get('address') || result.get('clientName') || 'Fraemohs home',
      _raw: result,
    };
  });
}
